import { ActivityPayload, ActivityType } from '@/components/Activities'
import { MergedContractPayload } from '@/modals/ContractModals/MergedContractModal/types'
import { ContractNegotiationForm } from '@/modals/NegotiationModals/CreateNegotiation'
import { Attachment } from '@/modules/Upload/types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import { sleep } from '@/utils/sleep'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import qs from 'query-string'
import { Option } from 'vnetwork-ui/dist/components/FormItem'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import type { CustomerCreateFormValues } from '../types'
import {
    ContractAndService,
    ContractNegotiation,
    ContactRoleCandidate,
    CreateCustomerMailer,
    CreateCustomerNotePayload,
    Customer,
    CustomerActive,
    CustomerAddress,
    CustomerAddressUpdatePayload,
    CustomerCalendarEvent,
    CustomerCalendarEventsPayload,
    CustomerContact,
    CustomerCoolLead,
    CustomerDeadLead,
    CustomerDetail,
    CustomerInvoice,
    CustomerInvoiceStatistical,
    CustomerJourney,
    CustomerJourneyExplaination,
    CustomerJourneyNote,
    CustomerLog,
    CustomerMailer,
    CustomerMailTask,
    CustomerQualifiedProspect,
    CustomerTrial,
    DeletedCustomer,
    GetCustomerJourneyParams,
    GetCustomerOptionsRow,
    GetListService,
    MailerType,
    NegotiationHistory,
    Service,
    ServicePOC,
    ServicePOCProps,
} from '../types'

export const createCustomer = async (data: CustomerCreateFormValues) => {
    const response = await datacentral.post('/customers', data)
    return response
}

export interface CustomerEmailExistsResponse {
    email: string
    exists_in_customers: boolean
    exists_in_customer_contacts: boolean
    exists_in_contact_sources: boolean
    exists: boolean
}

export const checkCustomerEmailExists = async (
    email: string
): Promise<CustomerEmailExistsResponse | null> => {
    try {
        const response = await datacentral.get<
            ResponseObject<CustomerEmailExistsResponse>
        >(`/customers/check-email?email=${encodeURIComponent(email)}`)
        return response?.data?.result || null
    } catch (error) {
        console.error(error)
        return null
    }
}

export interface CustomerPhoneExistsResponse {
    phone: string
    /** Chỉ check trùng với Lead Marketing (contact_sources) */
    exists_in_contact_sources: boolean
    exists: boolean
    lead_id?: number
    lead_name?: string
}

export const checkCustomerPhoneExists = async (
    phone: string
): Promise<CustomerPhoneExistsResponse | null> => {
    try {
        const response = await datacentral.get<
            ResponseObject<CustomerPhoneExistsResponse>
        >(`/customers/check-phone?phone=${encodeURIComponent(phone)}`)
        return response?.data?.result || null
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getCustomersQualifiedProspects = async (
    data: DataGridPayload
): Promise<ResponseArray<CustomerQualifiedProspect>> => {
    const response = await datacentral.get(
        `/customers/qualified-prospects?${qs.stringify(data)}`
    )
    return response?.data
}

export const getCustomerById = async (
    id: number
): Promise<ResponseObject<CustomerDetail>> => {
    const response = await datacentral.get(`/customers/${id}`)
    return response?.data
}

export const updateCustomer = async ({
    customer_id,
    customer_payload,
}: {
    customer_id: number
    customer_payload: Customer
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}`,
        customer_payload
    )
    return response?.data
}

export const getCustomerLogs = async ({
    customer_id,
    params,
}: {
    customer_id: number
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await datacentral.get(
        `/customers/${customer_id}/logs?${qs.stringify(params)}`
    )
    return response?.data
}

// Activity Logs API - aggregates logs from multiple sources
export interface ActivityLogFilters {
    page?: number
    page_size?: number
    types?: string // comma-separated log types
    gte?: string // start date YYYY-MM-DD
    lte?: string // end date YYYY-MM-DD
}

export interface ActivityLogItem {
    log_id: number
    log_type: string
    action_type: string
    description: string
    metadata?: Record<string, unknown>
    employee_id: number
    employee_name: string
    created_at: string
    customer_id: number
    ref_id?: number
}

export const getCustomerActivityLogs = async ({
    customer_id,
    params,
}: {
    customer_id: number
    params: ActivityLogFilters
}): Promise<ResponseArray<ActivityLogItem>> => {
    const response = await datacentral.get(
        `/customers/${customer_id}/activity-logs?${qs.stringify(params)}`
    )
    return response?.data
}

export const getCustomerJourney = async ({
    customer_id,
    params,
}: {
    customer_id: number
    params: GetCustomerJourneyParams
}): Promise<ResponseArray<CustomerJourney>> => {
    const response = await datacentral.get(
        `/customers/${customer_id}/customer-journey?${qs.stringify(params)}`
    )
    return response?.data
}

export const getCustomerJourneyById = async ({
    customer_id,
    customer_journey_id,
}: {
    customer_id: number
    customer_journey_id: number
}): Promise<ResponseObject<CustomerJourney>> => {
    const response = await datacentral.get(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}`
    )
    return response?.data
}

export const createCustomerJourney = async ({
    customer_id,
    params,
}: {
    customer_id: number
    params: {
        contact_id: number | null
        customer_journey_step?: string
        customer_journey_explaination: {
            sale_tracking_id: number[]
            service_interestings: string[]
        }
    }
}) => {
    const response = await datacentral.post(
        `/customers/${customer_id}/customer-journey`,
        params
    )
    return response?.data
}

export const updateCustomerJourneyExplaination = async ({
    customer_id,
    customer_journey_id,
    params,
}: {
    customer_id: number
    customer_journey_id: number
    params: CustomerJourneyExplaination
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/explaination`,
        params
    )
    return response?.data
}

export const createCustomerJourneyNote = async ({
    customer_id,
    customer_journey_id,
    params,
}: {
    customer_id: number
    customer_journey_id: number
    params: {
        note: Partial<CustomerJourneyNote>
        attachment?: Partial<Attachment>
    }
}) => {
    const response = await datacentral.post<
        ResponseObject<CustomerJourneyNote>
    >(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/note`,
        params
    )
    return response?.data
}

// Get journey notes with optional step filter
export const getCustomerJourneyNotes = async ({
    customer_id,
    customer_journey_id,
    params,
}: {
    customer_id: number
    customer_journey_id: number
    params?: {
        step?: string
        page?: number
        page_size?: number
    }
}): Promise<ResponseArray<CustomerJourneyNote>> => {
    const response = await datacentral.get<ResponseArray<CustomerJourneyNote>>(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/notes?${qs.stringify(
            params || {}
        )}`
    )
    return response?.data
}

export const updateCustomerJourneyNote = async ({
    customer_id,
    params,
    customer_journey_id,
}: {
    customer_id: number
    params: Partial<CustomerJourneyNote>
    customer_journey_id: number
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/note/${params?.customer_journey_note_id}`,
        params
    )
    return response.data
}

export const deleteCustomerJourneyNote = async ({
    customer_id,
    customer_journey_id,
    customer_journey_note_id,
}: {
    customer_id: number
    customer_journey_id: number
    customer_journey_note_id: number
}) => {
    const response = await datacentral.delete(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/note/${customer_journey_note_id}`
    )
    return response.data
}

export const getContactByCustomerId = async ({
    customer_id,
    params,
}: {
    customer_id: number
    params: DataGridPayload
}) => {
    const response = await datacentral.get<ResponseArray<CustomerContact>>(
        `/customers/${customer_id}/contacts?${qs.stringify(params)}`
    )
    return response?.data
}

export const getContactRoleCandidates = async (
    payload: DataGridPayload & { role: string; exclude_linked?: boolean }
): Promise<ResponseArray<ContactRoleCandidate>> => {
    const params: Record<string, any> = {
        role: payload.role,
        q: payload.q || '',
        page: payload.page || 1,
        page_size: payload.page_size || 10,
    }

    // Opt-in only: callers that want a "still to be processed" list pass exclude_linked, and the API
    // then drops candidates already converted for that role from BOTH the rows and the total. Omitted
    // by default so the reseller and supplier tabs keep listing linked + unlinked candidates alike.
    if (payload.exclude_linked) {
        params.exclude_linked = true
    }

    Object.keys(params).forEach((key) => {
        if (
            params[key] === '' ||
            params[key] === undefined ||
            params[key] === null
        ) {
            delete params[key]
        }
    })

    const response = await datacentral.get(
        `/customers/contacts/by-role?${qs.stringify(params)}`
    )

    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

export const getContactByCustomerIdOptions = async (
    customer_id: number,
    t: TFunction
): Promise<(Option & { contact_roles?: string[] })[]> => {
    const response = await datacentral.get<
        ResponseArray<{
            customer_contact_id: number
            contact_name: string
            contact_label: string[]
            contact_roles?: string[]
        }>
    >(`/customers/${customer_id}/contacts/options`)

    if (!response?.data?.results) return []

    return response?.data?.results?.map((e) => ({
        label:
            e?.contact_name +
            ' (' +
            e?.contact_label
                ?.map((label) =>
                    t(`labels.${label}`, {
                        ns: 'customer',
                        defaultValue: label,
                    })
                )
                .join(', ') +
            ')',
        value: e?.customer_contact_id,
        contact_roles: e?.contact_roles,
    }))
}

export const getContactById = async ({
    customer_id,
    id,
}: {
    customer_id: number
    id: number
}) => {
    const response = await datacentral.get<ResponseObject<CustomerContact>>(
        `/customers/${customer_id}/contacts/${id}`
    )
    return response?.data?.result
}

export const updateContact = async ({
    customer_id,
    contact_id,
    contact_payload,
}: {
    customer_id: number
    contact_id: number
    contact_payload: Partial<CustomerContact>
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/contacts/${contact_id}`,
        contact_payload
    )
    return response?.data
}

export const createContact = async ({
    customer_id,
    contact_payload,
}: {
    customer_id: number
    contact_payload: Partial<CustomerContact>
}) => {
    const response = await datacentral.post(
        `/customers/${customer_id}/contacts`,
        contact_payload
    )
    return response?.data
}

export const deleteContact = async ({
    customer_id,
    contact_id,
    message,
    auth_action,
}: {
    customer_id: number
    contact_id: number
    message: string
    auth_action?: 'keep' | 'unlink' | 'delete'
}) => {
    const response = await datacentral.delete(
        `/customers/${customer_id}/contacts/${contact_id}`,
        {
            data: { message, auth_action },
        }
    )
    return response?.data
}

/**
 * Update customer status
 * Only works when customer is in 'qualified-prospect' status
 * Valid target statuses: cool-lead, dead-lead, active, trial, stopped
 */
export const updateCustomerStatus = async ({
    customer_id,
    customer_status,
    customer_note,
}: {
    customer_id: number
    customer_status: string
    customer_note?: string
}) => {
    const response = await datacentral.put(`/customers/${customer_id}/status`, {
        customer_status,
        customer_note,
    })
    return response?.data
}

/**
 * Classify customer lead type
 * Valid lead types: warm-lead, sale-qualified-lead, cool-lead, un-qualified-lead
 * Backend chỉ chặn khi customer_status === 'active'; không có guard theo from-state,
 * nên mọi chiều chuyển đổi giữa 4 giá trị trên đều hợp lệ.
 * Auto-syncs to contact_sources if customer originated from marketing
 */
export const classifyCustomerLeadType = async ({
    customer_id,
    lead_type,
}: {
    customer_id: number
    lead_type: string
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/classify-lead`,
        { lead_type }
    )
    return response?.data
}

export const convertCustomer = async ({
    company_id,
    customer_id,
}: {
    customer_id: number
    company_id: number
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/convert`,
        {
            company_id,
        }
    )

    return response.data
}

export const getCustomerOptions = async (payload: DataGridPayload) => {
    const response = await datacentral.get<
        ResponseArray<GetCustomerOptionsRow>
    >(`/customers/options?${qs.stringify(payload)}`)

    if (!response?.data?.results) return []

    return response?.data?.results?.map((c) => ({
        label: [
            c.customer_id,
            c.company_name || c.customer_name,
            c.customer_email,
        ]
            .filter(Boolean)
            .join(' - '),
        value: c.customer_id,
    }))
}

export const getCustomerContains = async (payload: {
    customer_ids: number[]
}) => {
    const response = await datacentral.post<
        ResponseArray<GetCustomerOptionsRow>
    >(`/customers/contains`, payload)

    if (!response?.data?.results) return []

    return response?.data?.results?.map((c) => ({
        label: [
            c.customer_id,
            c.company_name || c.customer_name,
            c.customer_email,
        ]
            .filter(Boolean)
            .join(' - '),
        value: c.customer_id,
    }))
}

export const getCustomerCoolLead = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerCoolLead>>(
        `/customers/cool-lead?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getCustomerDeadLead = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerDeadLead>>(
        `/customers/dead-lead?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getCustomerActive = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerActive>>(
        `/customers/active?${qs.stringify(payload)}`
    )
    return response?.data
}

/** Bulk assign CS followers to multiple customers */
export const bulkAssignCustomerCS = async (payload: {
    customer_ids: number[]
    employee_ids: number[]
}) => {
    const response = await datacentral.post<
        ResponseObject<{ affected_count: number }>
    >('/customers/bulk-assign-cs', payload)
    return response?.data
}

export const getContractsByCustomerId = async (
    payload: DataGridPayload & { customer_id: number }
) => {
    const response = await datacentral.get<ResponseArray<ContractAndService>>(
        `/customers/${payload.customer_id}/contracts?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getServicesByCustomerId = async (
    payload: DataGridPayload & { customer_id: number; is_poc: boolean }
) => {
    const response = await datacentral.get<ResponseArray<GetListService>>(
        `/customers/${payload.customer_id}/services?${qs.stringify(payload)}`
    )
    return response?.data
}

// New unified services endpoint with filters, sorting, and pagination
export const getCustomerServices = async (payload: {
    customerID: number
    page?: number
    page_size?: number
    q?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    filters?: string
}) => {
    const { customerID, ...params } = payload
    const response = await datacentral.get<ResponseArray<Service>>(
        `/customers/${customerID}/all_services?${qs.stringify(params)}`
    )
    return response?.data
}

export const getCustomerInvoices = async (
    payload: DataGridPayload & { customerID: number }
) => {
    const response = await datacentral.get<ResponseArray<CustomerInvoice>>(
        `/customers/${payload?.customerID}/invoices?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getCustomerInvoiceStatistical = async ({
    customerID,
}: {
    customerID: number
}) => {
    const response = await datacentral.get<
        ResponseArray<CustomerInvoiceStatistical>
    >(`/customers/${customerID}/invoices/statistical`)
    return response?.data
}

export const getCustomersTrial = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerTrial>>(
        `/customers/trial?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getGroupMailerTypes = async () => {
    const response = await datacentral.get<ResponseArray<MailerType>>(
        `/customers/mailers/types`
    )
    return response?.data
}

export const getCustomerMailers = async ({
    customerID,
}: {
    customerID: number
}) => {
    const response = await datacentral.get<ResponseArray<CustomerMailer>>(
        `/customers/${customerID}/mailers`
    )
    return response?.data
}

export const createCustomerMailer = async ({
    customerID,
    data,
}: {
    customerID: number
    data: CreateCustomerMailer
}) => {
    const response = await datacentral.post<ResponseObject<CustomerMailer>>(
        `/customers/${customerID}/mailers`,
        {
            ...data,
        }
    )
    return response?.data
}

export const updateCustomerMailer = async ({
    customerID,
    customerMailerID,
    data,
}: {
    customerID: number
    customerMailerID: number
    data: CreateCustomerMailer
}) => {
    const response = await datacentral.put<ResponseObject<CustomerMailer>>(
        `/customers/${customerID}/mailers/${customerMailerID}`,
        {
            ...data,
        }
    )
    return response?.data
}

export const createServicePOC = async (
    values: Partial<
        ServicePOCProps & {
            products: Partial<ServicePOC>[]
        }
    >
) => {
    const response = await datacentral.post<
        ResponseObject<{ services: Service[] }>
    >(`/customers/${values.customer_id}/services/pocs`, {
        ...values,
    })
    return response?.data?.result ?? { services: [] }
}

export const convertPocServiceToOfficial = async ({
    customerID,
    serviceID,
}: {
    customerID: number
    serviceID: number
}) => {
    const response = await datacentral.post<ResponseObject<any>>(
        `/customers/${customerID}/services/pocs/${serviceID}/convert-official`
    )
    return response?.data
}

export const getCustomersCancelled = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerTrial>>(
        `/customers/cancelled?${qs.stringify(payload)}`
    )
    return response?.data
}

export const mergeInvoices = async (invoice_ids: number[]) => {
    const response = await datacentral.post('/invoices/merge', {
        invoice_ids,
    })
    return response?.data
}

export const mergeContracts = async (
    payload: MergedContractPayload & { merge_contract_ids: number[] }
) => {
    const response = await datacentral.post('/contracts/customers/merged', {
        ...payload,
        merge_contract_ids: payload?.merge_contract_ids,
    })
    return response?.data
}

export interface WhoisResponse {
    valid: boolean
    contact_id: number
    email: string
    authentication_method?: string
    authentication_id?: string
    user_uuid?: string
    error?: string
}

export const getWhoisByContactId = async (
    contact_id?: number
): Promise<WhoisResponse | null> => {
    try {
        if (!contact_id) {
            return null
        }

        const response = await datacentral.get(
            `/partner/auth/impersonate/whois?contact_id=${contact_id}`
        )

        return response.data
    } catch (error) {
        console.error('Failed to get whois by contact id:', error)
        return (error as any)?.response?.data?.result || null
    }
}

// ============ Account Management APIs ============

export interface KeycloakUserStatus {
    exists: boolean
    enabled: boolean
    email_verified: boolean
    user_id?: string
    username?: string
    created_at?: string
}

export interface LocalAuthUserStatus {
    exists: boolean
    user_id?: string
}

export interface AccountStatusResponse {
    contact_id: number
    email: string
    has_account: boolean
    authentication_method?: string
    authentication_id?: string
    keycloak_status?: KeycloakUserStatus
    local_auth_status?: LocalAuthUserStatus
}

export interface CreateAccountPayload {
    contact_id: number
    password: string
    password_confirmation: string
    send_welcome_email?: boolean
}

export interface CreateAccountResponse {
    success: boolean
    contact_id: number
    email: string
    authentication_id: string
    authentication_method: string
    message?: string
    welcome_email_sent: boolean
}

export interface SyncAccountResponse {
    contact_id: number
    email: string
    authentication_method: string
    authentication_id?: string
    previous_method?: string
    updated: boolean
    message?: string
}

// Get account status for a contact
export const getAccountStatus = async (
    contact_id: number
): Promise<AccountStatusResponse | null> => {
    try {
        const response = await datacentral.get<
            ResponseObject<AccountStatusResponse>
        >(`/partner/auth/account/status?contact_id=${contact_id}`)
        return response.data?.result || null
    } catch (error) {
        console.error('Failed to get account status:', error)
        return null
    }
}

// Create Keycloak account for existing contact
export const createKeycloakAccount = async (
    payload: CreateAccountPayload
): Promise<CreateAccountResponse | null> => {
    try {
        const response = await datacentral.post<
            ResponseObject<CreateAccountResponse>
        >('/partner/auth/account/create', payload)
        return response.data?.result || null
    } catch (error) {
        console.error('Failed to create Keycloak account:', error)
        throw error
    }
}

// Sync authentication method for a contact
export const syncAccount = async (
    contact_id: number
): Promise<SyncAccountResponse | null> => {
    try {
        const response = await datacentral.post<
            ResponseObject<SyncAccountResponse>
        >('/partner/auth/account/sync', { contact_id })
        return response.data?.result || null
    } catch (error) {
        console.error('Failed to sync account:', error)
        throw error
    }
}

// Link existing Keycloak user to contact
export const linkAccount = async (
    contact_id: number,
    authentication_id: string
) => {
    try {
        const response = await datacentral.post('/partner/auth/account/link', {
            contact_id,
            authentication_id,
        })
        return response.data?.result
    } catch (error) {
        console.error('Failed to link account:', error)
        throw error
    }
}

// Unlink authentication from contact
export const unlinkAccount = async (contact_id: number) => {
    try {
        const response = await datacentral.post(
            '/partner/auth/account/unlink',
            {
                contact_id,
            }
        )
        return response.data?.result
    } catch (error) {
        console.error('Failed to unlink account:', error)
        throw error
    }
}

// Set contact as primary
export const setPrimaryContact = async (
    customer_id: number,
    contact_id: number
) => {
    try {
        const response = await datacentral.post(
            `/customers/${customer_id}/contacts/${contact_id}/set-primary`
        )
        return response.data?.result
    } catch (error) {
        console.error('Failed to set primary contact:', error)
        throw error
    }
}

// Disable Keycloak account
export const disableAccount = async (contact_id: number, reason?: string) => {
    try {
        const response = await datacentral.post(
            '/partner/auth/account/disable',
            {
                contact_id,
                reason,
            }
        )
        return response.data?.result
    } catch (error) {
        console.error('Failed to disable account:', error)
        throw error
    }
}

// Enable Keycloak account
export const enableAccount = async (contact_id: number, reason?: string) => {
    try {
        const response = await datacentral.post(
            '/partner/auth/account/enable',
            {
                contact_id,
                reason,
            }
        )
        return response.data?.result
    } catch (error) {
        console.error('Failed to enable account:', error)
        throw error
    }
}

export const impersonateUser = async (contact_id?: number) => {
    try {
        const response = await datacentral.post('/partner/auth/impersonate', {
            contact_id,
        })

        await sleep(1000)

        return response.data
    } catch (error) {
        return (error as any)?.response
    }
}

export const getCustomerImportTemplate = async () => {
    try {
        return await datacentral.get(`/customers/import-template`, {
            responseType: 'blob',
        })
    } catch (error) {
        console.error('Lỗi khi tải file Excel:', error)
        throw error
    }
}

export const importCustomersFromExcel = async (file: File) => {
    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await datacentral.post(
            '/customers/import-excel',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        )

        return response.data
    } catch (error) {
        console.error('Lỗi khi import customers từ Excel:', error)
        throw error
    }
}

export const scanBusinessCards = async (images: File[]) => {
    const formData = new FormData()
    images.forEach((img) => formData.append('images', img))
    const response = await datacentral.post('/customers/scan-cards', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    })
    return response.data as {
        message: string
        result: import('../types').CardScanResponse
    }
}

// negotiation
export const createContractNegotiation = async ({
    data,
}: {
    data: Partial<ContractNegotiationForm> & { attachments: Attachment[] }
}) => {
    const response = await datacentral.post(`/contracts/negotiations`, {
        ...data,
    })
    return response?.data
}

export const getContractNegotiationByCustomerJourneyId = async (
    customer_journey_id: number
) => {
    const response = await datacentral.get<ResponseArray<ContractNegotiation>>(
        `/contracts/negotiations/customers/journeys/${customer_journey_id}`
    )
    return response?.data
}

export const getNegotiationHistory = async (id: number) => {
    const response = await datacentral.get<ResponseArray<NegotiationHistory>>(
        `/contracts/negotiations/${id}/histories`
    )
    return response?.data
}

export const updateNegotiationStatus = async ({
    id,
    data,
}: {
    id: string
    data: {
        to_status: string
        notes?: string
        reason?: string
        attachments?: any[]
    }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/history/status-update`,
        data
    )
    return response?.data
}

export const resolveNegotiation = async ({
    id,
    data,
}: {
    id: string
    data: { resolution_notes: string; attachments?: any[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/resolve`,
        data
    )
    return response?.data
}

export const rejectNegotiation = async ({
    id,
    data,
}: {
    id: number
    data: { reason: string; notes?: string; attachments?: Attachment[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/reject`,
        {
            ...data,
        }
    )
    return response?.data
}

export const cancelNegotiation = async ({
    id,
    data,
}: {
    id: string
    data: { reason: string; notes?: string; attachments?: any[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/history/cancel`,
        data
    )
    return response?.data
}

export const addNegotiationNote = async ({
    id,
    data,
}: {
    id: string
    data: { notes: string; attachments?: any[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/history/note`,
        data
    )
    return response?.data
}

export const addNegotiationAttachments = async ({
    id,
    data,
}: {
    id: string
    data: { attachments: any[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/attachments`,
        data
    )
    return response?.data
}

export const archiveNegotiation = async (negotiationID: number) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${negotiationID}/archive`
    )
    return response?.data
}

export const archiveCustomerJourney = async ({
    customer_id,
    customer_journey_id,
}: {
    customer_id: number
    customer_journey_id: number
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/archive`
    )
    return response?.data
}

export const updateCustomerJourneyStep = async ({
    customer_id,
    customer_journey_id,
    step,
}: {
    customer_id: number
    customer_journey_id: number
    step: string
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/step`,
        { customer_journey_step: step }
    )
    return response?.data
}

export const getCustomerCounts = async ({
    search,
    filters,
}: {
    search?: string
    filters?: string
}) => {
    const params = new URLSearchParams()
    if (search) params.append('q', search)
    if (filters) params.append('filters', filters)

    const response = await datacentral.get(
        `/customers/counts?${params.toString()}`
    )
    return response?.data
}

export const markDealAsLost = async ({
    customer_id,
    customer_journey_id,
    reason,
}: {
    customer_id: number
    customer_journey_id: number
    reason: string
}) => {
    const response = await datacentral.post(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/lost`,
        {
            reason,
        }
    )
    return response?.data
}

export const reEngageCustomerJourney = async ({
    customer_id,
    customer_journey_id,
    note,
}: {
    customer_id: number
    customer_journey_id: number
    note: string
}) => {
    const response = await datacentral.post(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/re-engage`,
        {
            note,
        }
    )
    return response?.data
}

// follow up note
export const updateFollowUpNote = async ({
    customer_id,
    customer_journey_id,
    note,
}: {
    customer_id: number
    customer_journey_id: number
    note: string
}) => {
    const response = await datacentral.put(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/follow-up`,
        {
            follow_up_note: note,
        }
    )
    return response?.data
}
//
export const deleteCustomerJourney = async ({
    customer_id,
    customer_journey_id,
    reason,
}: {
    customer_id: number
    customer_journey_id: number
    reason: string
}) => {
    const response = await datacentral.delete(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}`,
        {
            data: {
                reason,
            },
        }
    )
    return response?.data
}

export const deletePlainNotesOnlyCustomerJourney = async ({
    customer_id,
    customer_journey_id,
}: {
    customer_id: number
    customer_journey_id: number
}) => {
    const response = await datacentral.delete(
        `/customers/${customer_id}/customer-journey/${customer_journey_id}/plain-note-journey`
    )
    return response?.data
}

// create customer logs
export const createCustomerLogs = async ({
    customerID,
    content,
}: {
    customerID: number
    content: string
}) => {
    const response = await datacentral.post(`/customers/${customerID}/logs`, {
        view_content: content,
    })
    return response?.data
}

//export get customer name by customer id
export const getCustomerNameByCustomerId = async (customerID: number) => {
    const response = await datacentral.get<
        ResponseObject<{ customer_name: string }>
    >(`/customers/${customerID}/name`)
    return response?.data?.result
}

export const convertCustomerGreyMarket = async (customerId: number) => {
    const response = await datacentral.put(
        `/customers/${customerId}/grey-market`
    )
    return response?.data
}

export const convertNegotiation = async ({
    id,
    data,
}: {
    id: number
    data?: { notes?: string; attachments?: any[] }
}) => {
    const response = await datacentral.post(
        `/contracts/negotiations/${id}/convert`,
        data
    )
    return response?.data
}

// ============ Customer Notes API ============

// Create a customer note (automatically pinned)
export const createCustomerNote = async (
    customerId: number,
    payload: CreateCustomerNotePayload
) => {
    const response = await datacentral.post<ResponseObject<CustomerLog>>(
        `/customers/${customerId}/notes`,
        payload
    )
    return response.data.result
}

// Get pinned customer logs (notes only)
export const getPinnedCustomerLogs = async (customerId: number) => {
    const response = await datacentral.get<ResponseArray<CustomerLog>>(
        `/customers/${customerId}/logs/pinned`
    )
    return response.data.results || []
}

// Pin a customer log
export const pinCustomerLog = async (customerId: number, logId: number) => {
    const response = await datacentral.put<ResponseObject<CustomerLog>>(
        `/customers/${customerId}/logs/${logId}/pin`
    )
    return response.data.result
}

// Unpin a customer log
export const unpinCustomerLog = async (customerId: number, logId: number) => {
    const response = await datacentral.put<ResponseObject<CustomerLog>>(
        `/customers/${customerId}/logs/${logId}/unpin`
    )
    return response.data.result
}

// Update a customer note log
export const updateCustomerNoteLog = async (
    customerId: number,
    logId: number,
    payload: {
        note_description?: string
        is_pinned?: boolean
    }
) => {
    const response = await datacentral.put<ResponseObject<CustomerLog>>(
        `/customers/${customerId}/logs/${logId}/note`,
        payload
    )
    return response.data.result
}

// ============ Business - Journey Linking APIs ============

export interface UnlinkedBusiness {
    business_id: number
    business_type: string
    business_place: string
    business_startdate: string
    business_enddate: string
    business_content: string
    business_status: string
    business_created_at: string
    metadata?: {
        customer?: {
            customer_id?: number
            customer_journey_id?: number
        }
    }
}

// Get unlinked businesses for a customer
export const getUnlinkedBusinesses = async (customerId: number) => {
    const response = await datacentral.get<ResponseArray<UnlinkedBusiness>>(
        `/businesses/customers/${customerId}/unlinked`
    )
    return response?.data
}

// Link a business to a journey
export const linkBusinessToJourney = async ({
    businessId,
    journeyId,
}: {
    businessId: number
    journeyId: number
}) => {
    const response = await datacentral.put<ResponseObject<{ linked: boolean }>>(
        `/businesses/${businessId}/link-journey/${journeyId}`
    )
    return response?.data
}

// ==================== Customer Tickets ====================

export interface CustomerTicketsParams {
    limit?: number
    offset?: number
    status?: string
    start_date?: string
    end_date?: string
}

export interface CustomerTicketsResponse {
    message: string
    results: import('@/modules/Ticket/types/response').Ticket[]
    pagination: {
        total: number
        page: number
        page_size: number
        total_pages: number
        page_next: number
        page_prev: number
    }
}

// Get tickets for a customer
export const getCustomerTickets = async (
    customerId: number,
    params?: CustomerTicketsParams
) => {
    const response = await datacentral.get<CustomerTicketsResponse>(
        `/customers/${customerId}/tickets?${qs.stringify(params || {})}`
    )
    return response?.data
}

// Get email history/mailers for a customer
// Endpoint: GET /customers/:customerID/mailers/tasks
export const getCustomerMailTasks = async (
    payload: DataGridPayload & { customerId: number }
): Promise<ResponseArray<CustomerMailTask>> => {
    const { customerId, q, ...restPayload } = payload

    const response = await datacentral.get<ResponseArray<CustomerMailTask>>(
        `/customers/${customerId}/mailers/tasks?${qs.stringify({
            ...restPayload,
            search: q || undefined,
        })}`
    )
    return response?.data
}

export const confirmCustomerMailTaskReceived = async ({
    customerId,
    taskId,
}: {
    customerId: number
    taskId: number
}) => {
    const response = await datacentral.patch(
        `/customers/${customerId}/mailers/tasks/${taskId}/received`
    )
    return response?.data
}

// Delete customer (soft delete)
// Endpoint: DELETE /customers/:customerID
// Permission: customers:delete
export const deleteCustomer = async ({
    customer_id,
    deletion_reason,
}: {
    customer_id: number
    deletion_reason: string
}) => {
    const response = await datacentral.delete(`/customers/${customer_id}`, {
        data: { deletion_reason },
    })
    return response?.data
}

// Get deleted customers (Admin only)
// Endpoint: GET /customers/deleted
// Permission: customers:index + is_admin
export const getDeletedCustomers = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<DeletedCustomer>>(
        `/customers/deleted?${qs.stringify(payload)}`
    )
    return response?.data
}

// ============ Customer Address APIs ============

// Get customer address
// Endpoint: GET /customers/:customerID/address
// Permission: customers:show
export const getCustomerAddress = async (
    customerId: number
): Promise<ResponseObject<CustomerAddress>> => {
    const response = await datacentral.get<ResponseObject<CustomerAddress>>(
        `/customers/${customerId}/address`
    )
    return response?.data
}

// Update customer address
// Endpoint: PUT /customers/:customerID/address
// Permission: customers:update
// Constraints: Customer không bị lock
export const updateCustomerAddress = async ({
    customerId,
    payload,
}: {
    customerId: number
    payload: CustomerAddressUpdatePayload
}): Promise<ResponseObject<CustomerAddress>> => {
    const response = await datacentral.put<ResponseObject<CustomerAddress>>(
        `/customers/${customerId}/address`,
        payload
    )
    return response?.data
}

// ============ Customer Revenue Allocation APIs ============

export interface CustomerRevenueOverviewParams {
    gte?: string // From month (YYYY-MM)
    lte?: string // To month (YYYY-MM)
}

export interface ProductGroupMonthlyRevenue {
    product_group_name: string
    monthly_data: Record<
        string,
        {
            allocated_previous_year: number
            allocated_current_year: number
            unallocated: number
        }
    >
    allocated_renewal: number
    allocated_new_sale: number
    allocated_upsell: number
    unallocated_renewal: number
    unallocated_new_sale: number
    unallocated_upsell: number
    total_revenue: number
}

export interface CustomerRevenueOverviewResponse {
    product_groups: ProductGroupMonthlyRevenue[]
    months: string[] // List of months in range (YYYY-MM)
    grand_total: number
}

// Get customer revenue overview (pivot by product group and month)
// Fetches all data and aggregates on client-side for pivot table
export const getCustomerRevenueOverview = async (
    customerId: number,
    params?: CustomerRevenueOverviewParams
): Promise<CustomerRevenueOverviewResponse> => {
    // Fetch all revenue data with high page_size
    const response = await getCustomerRevenueCombined(customerId, {
        ...params,
        page: 1,
        page_size: 10000, // Get all records
        sort_by: 'product_group_name',
        sort_order: 'asc',
    })

    const results = response?.results || []

    // Generate months list from params
    const months: string[] = []
    if (params?.gte && params?.lte) {
        const startDate = dayjs(params.gte)
        const endDate = dayjs(params.lte)
        let current = startDate
        while (
            current.isBefore(endDate) ||
            current.format('YYYY-MM') === endDate.format('YYYY-MM')
        ) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }
    }

    // Group by product_group_name
    const productGroupMap = new Map<string, ProductGroupMonthlyRevenue>()

    // Revenue type field mapping to reduce branching
    const ALLOCATED_TYPE_KEYS: Record<
        string,
        keyof ProductGroupMonthlyRevenue
    > = {
        renewal: 'allocated_renewal',
        new_sale: 'allocated_new_sale',
        upsell: 'allocated_upsell',
    }
    const UNALLOCATED_TYPE_KEYS: Record<
        string,
        keyof ProductGroupMonthlyRevenue
    > = {
        renewal: 'unallocated_renewal',
        new_sale: 'unallocated_new_sale',
        upsell: 'unallocated_upsell',
    }

    const defaultGroup = (): ProductGroupMonthlyRevenue => ({
        product_group_name: '',
        monthly_data: {},
        allocated_renewal: 0,
        allocated_new_sale: 0,
        allocated_upsell: 0,
        unallocated_renewal: 0,
        unallocated_new_sale: 0,
        unallocated_upsell: 0,
        total_revenue: 0,
    })

    results.forEach((item) => {
        const groupName = item.product_group_name || 'Khác'
        if (!productGroupMap.has(groupName)) {
            productGroupMap.set(groupName, {
                ...defaultGroup(),
                product_group_name: groupName,
            })
        }

        const group = productGroupMap.get(groupName)!
        const amount = item.allocated_amount || item.revenue_amount || 0
        const month = item.revenue_month

        if (!group.monthly_data[month]) {
            group.monthly_data[month] = {
                allocated_previous_year: 0,
                allocated_current_year: 0,
                unallocated: 0,
            }
        }

        const monthData = group.monthly_data[month]
        if (item.is_allocated) {
            const yearKey = item.is_from_previous_year
                ? 'allocated_previous_year'
                : 'allocated_current_year'
            monthData[yearKey] += amount
            const typeKey = ALLOCATED_TYPE_KEYS[item.revenue_type]
            if (typeKey) (group[typeKey] as number) += amount
        } else {
            monthData.unallocated += amount
            const typeKey = UNALLOCATED_TYPE_KEYS[item.revenue_type]
            if (typeKey) (group[typeKey] as number) += amount
        }

        group.total_revenue += amount
    })

    const productGroups = Array.from(productGroupMap.values()).sort((a, b) =>
        a.product_group_name.localeCompare(b.product_group_name)
    )

    const grandTotal = productGroups.reduce(
        (sum, g) => sum + g.total_revenue,
        0
    )

    return {
        product_groups: productGroups,
        months,
        grand_total: grandTotal,
    }
}

export interface CustomerRevenueCombinedParams {
    gte?: string // From month (YYYY-MM)
    lte?: string // To month (YYYY-MM)
    search?: string
    revenue_type?: string
    is_projected?: boolean
    is_allocated?: boolean
    is_paid?: boolean
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
}

export interface CustomerRevenueCombinedResult {
    id: number
    customer_id: number
    customer_name: string
    service_id: number
    service_name: string
    product_group_name: string
    revenue_month: string
    total_amount: number
    allocated_amount?: number
    revenue_amount?: number
    is_projected: boolean
    is_paid: boolean
    is_allocated: boolean
    is_from_previous_year?: boolean
    account_manager_code?: number
    sale_manager_code?: number
    invoice_id?: number
    revenue_type?: string
    payment_date?: string
}

export interface CustomerRevenueCombinedPagination {
    page: number
    page_size: number
    total: number
    total_pages: number
}

export interface CustomerRevenueCombinedSummary {
    total_records: number
    total_actual_records: number
    total_projected_records: number
    total_allocated_amount: number
    total_actual_amount: number
    total_projected_amount: number
    total_paid_records: number
    total_unpaid_records: number
    total_paid_amount: number
    total_unpaid_amount: number
}

export interface CustomerRevenueCombinedResponse {
    results: CustomerRevenueCombinedResult[]
    pagination: CustomerRevenueCombinedPagination
    summary: CustomerRevenueCombinedSummary
}

// Get combined revenue (actual + projected) for a specific customer
// Endpoint: GET /reports/bi/revenue-combined/customer/:customer_id
export const getCustomerRevenueCombined = async (
    customerId: number,
    params?: CustomerRevenueCombinedParams
): Promise<CustomerRevenueCombinedResponse> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get(
        `/reports/bi/revenue-combined/customer/${customerId}?${queryString}`
    )
    return data?.result || { results: [], pagination: null, summary: null }
}

// Export customer revenue combined data to Excel
export const exportCustomerRevenueCombined = async (
    customerId: number,
    params?: CustomerRevenueCombinedParams
): Promise<void> => {
    const queryString = qs.stringify({ ...params, export: 'excel' })
    const response = await datacentral.get(
        `/reports/bi/revenue-combined/customer/${customerId}?${queryString}`,
        {
            responseType: 'blob',
        }
    )

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = `customer_${customerId}_revenue_${new Date().toISOString().split('T')[0]}.xlsx`

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
        }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

/**
 * Lịch sự kiện khách hàng theo tháng.
 *
 * Backend tự quyết phạm vi: BOD/BOM/Leader/Admin nhận toàn bộ, còn lại chỉ nhận khách mà mình
 * nằm trong tập follower. Người không follow ai nhận mảng rỗng kèm HTTP 200 — KHÔNG phải 403,
 * nên đừng bắt lỗi ở đây.
 */
export const getCustomerCalendarEvents = async (
    payload: CustomerCalendarEventsPayload
) => {
    const response = await datacentral.get<
        ResponseArray<CustomerCalendarEvent>
    >(`/customers/calendar-events?${qs.stringify(payload)}`)
    return response?.data
}
